import { FC, HTMLAttributes, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { VodacomRewardCard } from "../VodacomRewardCard/VodacomRewardCard";
import { CoinNumber } from "../../core/CoinNumber/CoinNumber";
import {
  TierLevel,
  CoinWallet,
  Achievement as AchievementType,
} from "@onmo/graphql/graphql";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { RankColor } from "../../../constants/user";
import { User } from "../../../models/user";
import { convertAchievement } from "../../../models/achievement";
import { useAppSelector } from "../../hooks";
import { useStyles } from "./DailyRewardCategory.style";

interface IDailyRewardCategoryProps {
  wallets: CoinWallet[];
  newAchievement?: AchievementType;
  tierLevels: TierLevel[];
}

export const DailyRewardCategory: FC<
  HTMLAttributes<HTMLDivElement> & IDailyRewardCategoryProps
> = (props) => {
  const classes = useStyles();
  const { wallets, newAchievement, tierLevels } = props;
  const { t } = useTranslation();
  const { achievements } = useAppSelector((state) => state.user);
  const userRank = useMemo(() => {
    return User.getCurrentRank(wallets, tierLevels);
  }, [wallets, tierLevels]);
  const oldAchievement = useMemo(() => {
    return achievements?.find(
      (a: AchievementType) => a.id === newAchievement?.id,
    );
  }, [achievements, newAchievement]);
  const oldFullAchievement = useMemo(() => {
    if (!oldAchievement) return;
    return convertAchievement(oldAchievement);
  }, [oldAchievement]);
  const newFullAchievement = useMemo(() => {
    if (!newAchievement) return;
    return convertAchievement(newAchievement);
  }, [newAchievement]);

  return (
    <div>
      {userRank?.nextLevel && (
        <div>
          <Typography
            variant={TypographyVariant.BodyB4Bold}
            className={classes.title}
          >
            {t("common_Reward")}
          </Typography>
          <VodacomRewardCard
            imageSrc={userRank?.icon}
            titleContent={
              <>
                <CoinNumber
                  amount={userRank?.nextLevel.xp - User.getGzCoin(wallets)}
                  size={16}
                  type="gzcoins"
                  reverse
                />
                {t("commom_to_rannk", { rank: userRank?.nextLevel.name })}
              </>
            }
            progress={userRank.levelPercent}
            progressColor={userRank.color || RankColor.UnRanked}
          />
        </div>
      )}
      {userRank && (
        <div className={classes.section}>
          <Typography
            variant={TypographyVariant.BodyB4Bold}
            className={classes.title}
          >
            {t("common_Achievements")}
          </Typography>
          <div className={classes.content}>
            {oldFullAchievement?.achievementImage && (
              <VodacomRewardCard
                imageSrc={oldFullAchievement.achievementImage}
                titleContent={t(oldFullAchievement?.description || "", {
                  amount: oldFullAchievement.currentTier,
                })}
                progress={oldFullAchievement.progressPercentage}
                progressColor={oldFullAchievement.color || ""}
              />
            )}
            {newFullAchievement?.achievementImage && (
              <VodacomRewardCard
                imageSrc={newFullAchievement.achievementImage}
                titleContent={t(newFullAchievement?.description || "", {
                  amount: newFullAchievement.currentTier,
                })}
                progress={newFullAchievement.progressPercentage}
                progressColor={newFullAchievement.color || ""}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
